/**
 * CMS marker keys → accommodation ids. RETIRE-WITH-PRICECALCULATOR.
 *
 * The CMS author types a marker into a tile's caption and the capture group is
 * the key:
 *
 *     <span class="at-block__foto-subtitle">{[2026/verhuur/lodge/zomerlodge.json]}</span>
 *
 * `compat-protocol.md` says the keys are ours to choose because the captions are
 * editable, which is true and is why there is no translation table here. What it
 * gets wrong is what they currently ARE: not `{[zomerlodge]}` but the old
 * calculator's tariff config path, `.json` and all — the same path that appears
 * in `/api/tariffs/{path}` — and with an optional variant segment, so they are
 * two or three deep depending on the accommodation.
 *
 * ---------------------------------------------------------------------------
 * THE REDUCTION IS NO LONGER THE WHOLE ANSWER
 * ---------------------------------------------------------------------------
 *
 * This file first took the LAST SEGMENT — `zomerlodge` — on the stated ground
 * that it "happens to land on the slug we already use". It did not: the 2026
 * port derived its ids from the same legacy paths by dropping the YEAR and
 * joining everything after it, so the marker above meant
 * `verhuur-lodge-zomerlodge`. Under the last-segment rule every live marker
 * resolved to an id that existed nowhere, except `trekkershut`, which resolved
 * to a DIFFERENT accommodation and would have been priced from its tariffs.
 *
 * Those port-derived ids are gone too. They are why an accommodation labelled
 * "Triumph 2" was called `verhuur-caravan-waterkant-chalet`, and the park's ids
 * now follow the labels an operator maintains — see `tools/handoff-2027.js`.
 *
 * So the reduction gets a marker as far as the id it USED to mean, and
 * `KEY_ALIASES` carries it the rest of the way. Both halves are needed and
 * neither is guessable: `2026/verhuur/caravan/waterkant-chalet.json` resolves
 * to `triumph-2`, which no rule about paths would produce.
 *
 * A page written now should skip all of it and say `{[triumph-2]}` — the id
 * itself, which passes straight through. When the CMS captions are rewritten
 * that way, this file and its alias table retire together.
 */

import { KEY_ALIASES } from './vergarde.js';

/**
 * The path after the year, joined with `-`, without a `.json` suffix.
 *
 * `2026/verhuur/lodge/zomerlodge.json` → `zomerlodge`     (path, then alias)
 * `2026/verhuur/caravan/waterkant-chalet.json` → `triumph-2`
 * `triumph-2`                          → `triumph-2`      (a 2027 caption)
 *
 * A leading YEAR is what is dropped, not a leading segment: a caption that has
 * already been rewritten to an id has no year on it, and blindly dropping the
 * first segment would eat the `verhuur` out of one.
 */
export function keyToAccommodationId(key: string): string {
  // `Object.hasOwn` and not `KEY_ALIASES[key] !== undefined`: the keys are typed
  // by a person into a CMS caption, and `{[constructor]}` or `{[toString]}` would
  // otherwise find something on `Object.prototype` and return a function.
  if (Object.hasOwn(KEY_ALIASES, key)) return KEY_ALIASES[key]!;

  const parts = key.replace(/\.json$/i, '').split('/').filter((p) => p !== '');
  if (parts.length > 1 && /^\d{4}$/.test(parts[0]!)) parts.shift();
  const reduced = parts.join('-');

  // And again after reducing, so an alias can be written once against the id a
  // marker resolves to rather than once per year it appears in. The live pages
  // carry `2026/...`; the same accommodation on a 2027 page would otherwise
  // need a second entry saying the same thing.
  return Object.hasOwn(KEY_ALIASES, reduced) ? KEY_ALIASES[reduced]! : reduced;
}
